import React, { ReactNode, useEffect, useMemo, useState } from 'react';



import { Badge, Box, Button, FormField, Link, Pagination, Popover, SpaceBetween, Table, TextFilter, TruncatedText } from '@cloudscape-design/components';
import { mdiAlphaSCircle, mdiFileExcel, mdiHumanMaleBoard, mdiMagnify } from '@mdi/js';
import Icon from '@mdi/react';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { formatCurrency, minutes2Hours, minutesToHHMM2, setParameters } from '../../pages/commons/functions';
import { useGlobalState } from '../../pages/context/GlobalStateContext';
import { btnIconForm, ItemFlight } from '../../resources/types';
import BadgeAltitudes from '../extras/BadgeAltitudes';
import BadgeBookingType from '../extras/BadgeBookingType';
import BadgeCustom from '../extras/BadgeCustom';
import SelectPaging from '../selects/selectPaging';


interface TableFlightsProps {
    flights?: ItemFlight[];
    visible?: boolean;
    showPilot?: boolean;
    showInstructor?: boolean;
    showEquipment?: boolean;
    showSoftMantto?: boolean;
    showInvoice?: boolean;
    showObs?: boolean;
    footer?: boolean;
    excel?: boolean;
    filter?: boolean;
    refresh?: boolean;
    hidePaging?: boolean;
    onRefresh?: () => void; // ← optional now
    onSelect?: (selectedRow: any) => void; // ← optional now
    onRangeChange?: (startDate: string, endDate: string) => void; // ← optional now
    records?: number;
    dateFilter?: "PERIOD" | "RANGE" | "NONE";
    //onDismiss: () => void;
    //onConfirm: (selectedDate: string) => void;
}

const TableFlights: React.FC<TableFlightsProps> = ({
    flights = [],
    visible = true,
    filter = true,
    showPilot = true,
    showInstructor = true,
    showEquipment = true,
    hidePaging = false,
    showSoftMantto = false,
    showInvoice = false,
    showObs = true,
    footer = false,
    excel = false,
    onSelect,
    onRefresh,
    onRangeChange,
    dateFilter = "RANGE",
    refresh = true
}) => {

    const { parameters } = useGlobalState();

    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [filterValue, setFilterValue] = useState("");
    const filtered: ItemFlight[] = flights.filter((item) => {

        const normalize = (str: string | undefined | null) =>
            (str || "")
                .toString()
                .toLowerCase()
                .normalize("NFD") // Decomposes accented characters (e.g., 'í' -> 'i' + '◌́')
                .replace(/[\u0300-\u036f]/g, "") // Removes the accent marks
                .replace(/[^a-z0-9]/g, ""); // Removes non-alphanumeric

        const searchWords = normalize(filterValue).split(/\s+/).filter(Boolean);
        const text = normalize([item.userPilot ?? "", item.userInstructor ?? "", item.flightType ?? "", (item.registration ?? "").toString(), (item.idFlight ?? "").toString(), (item.registrationTow ?? "").toString()].join(" "));
        return searchWords.every((word) => text.includes(normalize(word)));
    });
    const paginated = filtered.slice((currentPageIndex - 1) * parameters.pageSize, currentPageIndex * parameters.pageSize);


    const exportToExcel = () => {
        // Convert JSON to worksheet

        let mExcel: any[] = (filtered.map((fl: ItemFlight) => ({
            ID: fl.idFlight,
            IDSM: fl.jsonSoftMantto.id ?? "",
            Salida: fl.departureDate + " " + fl.departureTime,
            Llegada: fl.arrivalDate + " " + fl.arrivalTime,
            Piloto: fl.userPilot,
            Instructor: fl.userInstructor,
            Material: fl.registration,
            Tipo: fl.flightType,
            Remolque: fl.altitude > 0 ? fl.altitude : "",
            TPO: minutes2Hours(fl.delta ?? 0),
            Observaciones: fl.obs ?? ""
        })));
        const ws = XLSX.utils.json_to_sheet(mExcel);

        // Create a workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Flights");

        // Generate XLSX file and download
        XLSX.writeFile(wb, "flights.xlsx");
    };
    const exportTableToPDF = (data: any[], columns: string[]) => {
        const doc = new jsPDF();

        doc.text("Movements", 14, 10);

        autoTable(doc, {
            head: [columns],
            body: data.map(row => columns.map(col => row[col])),
        });

        doc.save("movements.pdf");
    };

    const flightObs = (item: ItemFlight): ReactNode => {
        const textLong = item.obs ?? "";

        if (textLong.length > 30) {
            return (
                <span title={textLong} style={{ display: 'inline-block', width: '100%' }}>
                    <TruncatedText>{textLong}</TruncatedText>
                </span>
            );
        } else {
            return (<span>{textLong}</span>);
        }
    };

    const columns = useMemo(() => [
        {
            id: 'idFlight',
            header: 'ID/Fecha',
            cell: (item: ItemFlight) => <FormField label={
                onSelect ?
                    <Link onClick={() => {
                        onSelect(item)
                    }}>
                        <div>
                            {item.idFlight} {showSoftMantto && item.jsonSoftMantto.id ? <Icon path={mdiAlphaSCircle} size={0.7} /> : null}</div></Link> : item.idFlight}>
                {item.departureDate}

            </FormField>,
            width: 120,
        },
        {
            id: 'duration',
            header: 'TPO',
            cell: (item: ItemFlight) => <FormField label={minutesToHHMM2(item.delta ?? 0)}>{minutes2Hours(item.delta).toFixed(2)} h</FormField>,
            width: 120,
        },
        showEquipment ?
            {
                id: 'aircraft',
                header: 'Material',
                cell: (item: ItemFlight) => (
                    // 🔑 FIX 2: Add key to the outermost component in the cell
                    <FormField key={`aircraft-formfield-${item.idFlight}`} label={item.registration}>
                        {item.groupTypeMaster}
                    </FormField>
                ),
                maxWidth: 140,
            } : null,
        showPilot || showInstructor ?
            {
                id: 'departure',
                header: showPilot && showInstructor ? 'Tripulación' : showPilot && !showInstructor ? 'Piloto' : 'Instructor',
                cell: (item: ItemFlight) => (
                    <>
                        {showPilot && showInstructor ? (
                            <FormField label={item.userPilot} key={`labelinstructor-${item.idFlight}`}>
                                {item.idUserInstructor > 0 ? (
                                    <SpaceBetween direction="horizontal" size="xs" key={`instructor-spacebetween-${item.idFlight}`}>
                                        {/* 🔑 FIX 1: Add keys to the children of SpaceBetween */}
                                        <Icon path={mdiHumanMaleBoard} size={1} key={`icon-${item.idFlight}`} />
                                        <span key={`instructor-name-${item.idFlight}`}>{item.userInstructor}</span>
                                    </SpaceBetween>
                                ) : null}
                            </FormField>
                        ) : null}

                        {showPilot && !showInstructor ? (
                            <FormField label={item.userPilot} key={`labelinstructor-${item.idFlight}`}>
                                {item.idUserInstructor > 0 ? (

                                    <FormField label={item.userPilot} key={`labelinstructor-${item.idFlight}`} />
                                ) : (
                                    // Always return a valid React element or null/'' if conditionally rendering
                                    ''
                                )}
                            </FormField>
                        ) : null}

                        {!showPilot && showInstructor ? (

                            <SpaceBetween direction="horizontal" size="xs" key={`instructor-spacebetween-${item.idFlight}`}>
                                {/* 🔑 FIX 1: Add keys to the children of SpaceBetween */}
                                <Icon path={mdiHumanMaleBoard} size={1} key={`icon-${item.idFlight}`} />
                                <span key={`instructor-name-${item.idFlight}`}>{item.userInstructor}</span>
                            </SpaceBetween>
                        ) : null}

                    </>
                ),
            } : null,
        {
            id: 'flightType',
            header: 'Etiquetas',
            cell: (item: ItemFlight) => <SpaceBetween direction="horizontal" size="xxxs">
                <BadgeBookingType idBookingType={item.idFlightType} type="min" size="small" />
                <BadgeAltitudes flight={item} type="min" size="small" />

                {item.obs && item.obs.length > 0 && showObs ? <BadgeCustom color="orange" size="small"><Popover content={item.obs ?? ""} header={"Observaciones"} triggerType="custom">OBS</Popover></BadgeCustom> : null}
            </SpaceBetween>,
            minWidth: 140
        },
        showInvoice ?
            {
                id: "invoice",
                header: "Facturación",
                width: 140,
                cell: (item: ItemFlight) => (
                    <>
                        {item.isInvoiced === 0 && item.isInvoice === 1 && (
                            <Badge color='blue'>Por Facturar</Badge>
                        )}
                        {item.isInvoiced === 1 && item.isInvoice === 1 && (
                            <FormField
                                description={item.cost && item.cost > 0 ? `MOV #${item.idMovement}` : null}
                                label={item.costReal && item.costReal > 0 ? formatCurrency(item.costReal, "CLP") : "------"}
                            />
                        )}
                    </>
                )
            } : null

    ].filter(Boolean), [flights, paginated, filtered, currentPageIndex, parameters.pageSize, filterValue]);
    useEffect(() => {
        setCurrentPageIndex(1);
    }, [flights]);
    const dataFlights = hidePaging ? filtered : paginated;
    return (
        <>
            <Table

                variant="embedded"
                cellVerticalAlign='top'
                trackBy="idFlight"

                stickyHeader
                filter={
                    <>
                        <SpaceBetween direction='horizontal' size='xs'>
                            {/**
                            {dateFilter === "RANGE" && (

                                <DRPicker start={parameters.startDate} end={parameters.endDate}
                                    onConfirm={(startDate: string, endDate: string) => {
                                        if (startDate && endDate) {
                                            const updatedParameters = { ...parameters, startDate: startDate, endDate: endDate, };
                                            setParameters(updatedParameters);
                                        } else {
                                            const updatedParameters = { ...parameters, startDate: "", endDate: "", };
                                            setParameters(updatedParameters);
                                        }
                                    }}
                                />
                            )}
                            {dateFilter === "PERIOD" && (

                                <div style={{ width: 130 }}>
                                    <MPModal
                                        width={120}
                                        today prevMonth nextMonth
                                        end={new Date()}
                                        date={`${parameters.period}`}
                                        onConfirm={(date) => {
                                            setParameters({ ...parameters, period: date.period, month: date.month, year: date.year });
                                        }}
                                    />
                                </div>
                            )}
                                 */}
                            {filter ? (

                                <div style={{ width: 300 }}>
                                    <TextFilter countText={filterValue === "" ? `${flights.length} registros` : `${paginated.length}/${flights.length}`}
                                        filteringPlaceholder="Filtrar"
                                        filteringText={filterValue}
                                        disabled={flights.length === 0}
                                        onChange={({ detail }) => {
                                            setFilterValue(detail.filteringText);
                                            setCurrentPageIndex(1);
                                        }}
                                    />
                                </div>
                            ) : undefined}
                            {excel && (
                                <Button disabled={flights.length === 0} onClick={exportToExcel} style={{ root: btnIconForm }}><Icon path={mdiFileExcel} size={1} /></Button>
                            )}
                            {!!onRefresh && (
                                <Button onClick={() => setParameters({ ...parameters, dummy: new Date() })} style={{ root: btnIconForm }}><Icon path={mdiMagnify} size={1} /></Button>
                            )}
                        </SpaceBetween>
                    </>
                }

                footer=
                {footer && flights.length > 0 && (<SpaceBetween direction="horizontal" size="xs">
                    <div>
                        Número de vuelos: <strong>{flights.length}</strong>
                    </div>
                    <div>
                        Tiempo total: <strong>{flights.reduce((acc, flight) => acc + (flight.delta ?? 0), 0) ? minutesToHHMM2(flights.reduce((acc, flight) => acc + (flight.delta ?? 0), 0)) : '00:00'}</strong>
                    </div>
                    <div>
                        Horas total: <strong>{flights.reduce((acc, flight) => acc + (flight.delta ?? 0), 0) ? minutes2Hours(flights.reduce((acc, flight) => acc + (flight.delta ?? 0), 0)) : '0.00'} h</strong>
                    </div>
                </SpaceBetween>)
                }
                pagination={
                    !hidePaging ?
                        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                            {filtered.length > parameters.pageSize && (
                                <Pagination
                                    currentPageIndex={currentPageIndex}
                                    pagesCount={Math.ceil(filtered.length / parameters.pageSize)}
                                    onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
                                />
                            )}

                            {flights.length > 0 && (
                                <SelectPaging onSelect={(pageSize) => {
                                    setCurrentPageIndex(1);
                                    setParameters({ ...parameters, pageSize: pageSize });
                                }} />
                            )}
                        </SpaceBetween>
                        : null
                }
                columnDefinitions={columns}
                items={dataFlights ?? []}
                empty={
                    <Box
                        margin={{ vertical: "xs" }}
                        textAlign="center"
                        color="inherit"
                    >
                        <SpaceBetween size="m">
                            <b>Sin vuelos asociados</b>
                        </SpaceBetween>
                    </Box>
                }
            />
        </>
    );
};

export default TableFlights;